import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from './Badge';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Tooltip } from './Tooltip';
import { cx } from '@/lib/cx';
import type { ColumnLayoutController } from '@/lib/use-column-layout';

/**
 * The column manager: show/hide, freeze to either edge, and reorder.
 *
 * ## Rules this obeys rather than reinvents
 *
 * **Rung 60, portalled.** The trigger sits above a `TableWrap`, whose
 * `overflow-x-auto` would clip a popover rendered in place, and the table's own
 * frozen header cells are `z-20` in the same stacking context. Rendering into
 * `document.body` at rung 60 escapes both. ⚠️ **Raising the number instead does
 * nothing**: a z-index inside a stacking context is local, which is the trap the
 * layer ladder in `src/index.css` exists to state.
 *
 * **`place()` MEASURES and is idempotent.** It reruns on every scroll and
 * resize — the table scrolls sideways underneath it. A provisional width
 * corrected by a second effect was measured at 320px stranding options past the
 * viewport edge at right 334, and because the popover is `position: fixed` that
 * overhang adds no page scroll to recover it.
 *
 * **No helper text.** No "drag to reorder", no explanation of what freezing
 * does. The only sentences here are the ones the prohibition's own exception
 * list allows: **why a control is disabled**. Each appears as a `title` — on the
 * last remaining visible column, on a locked one, and on `Show all` when every
 * hidden column is locked. ⚠️ **Count them in the source, never here**: this
 * line read "Both" until a third arrived and nothing failed.
 *
 * **Three affordances, two input methods, and the split is not a fallback.** The
 * GRIP is pointer-only and hides itself on a coarse pointer — ⚠️ native HTML5
 * drag does not work on touch AT ALL, which the kanban boards learned and paid
 * for with a per-card Move menu. MOVE UP / MOVE DOWN are the touch path AND the
 * keyboard path. The grip is `aria-hidden` decoration: a button whose click does
 * nothing is worse than no button.
 *
 * **The hidden count is WORDED, never a bare tint** — colour is never the only
 * signal here, and the kanban `Columns` control already sets the precedent of a
 * worded badge beside the trigger.
 */
export interface ColumnManagerProps {
  /** The controller from `useColumnLayout`. */
  layout: ColumnLayoutController;
  /** Human label per column key — the same string the header shows. */
  labelOf: (key: string) => string;
  /** Keys that may never be hidden or moved. Listed, disabled, never omitted. */
  lockedKeys?: readonly string[];
  /**
   * Why a given key is locked, in the reader's terms.
   *
   * ⚠️ **A LOCK HAS SEVERAL REASONS AND ONE SENTENCE CANNOT COVER THEM.** The
   * actions lane is locked because it holds every per-record control; a Group
   * column is locked because it is the only place membership can be edited; an
   * identifying column is locked because a table of timestamps with no names is
   * not a narrower view, it is a useless one. A fixed string was right about the
   * first and approximately wrong about the rest — reported by the second page
   * to adopt this. Falls back to a general sentence when a caller says nothing.
   *
   * This is a **why-this-is-disabled** sentence, which is one of the four kinds
   * the no-helper-text rule explicitly keeps: a bare `disabled` makes a locked
   * column and a broken switch identical.
   */
  lockReasonOf?: (key: string) => string | undefined;
  /**
   * Keys the page already renders with `pinned`. Their freeze controls are
   * omitted — that edge is not the reader's to choose.
   *
   * ⚠️ **NOT the same set as `lockedKeys`, and treating them as one broke the
   * feature's main use.** A locked column cannot be hidden or moved; that says
   * nothing about which edge it sits at. Pages lock their identifying column —
   * which is the one a reader most wants frozen to the left.
   */
  pinnedKeys?: readonly string[];
  className?: string;
  /**
   * The trigger's word and the panel's heading. Defaults to `Columns`. The
   * revenue matrices also mount this for ROWS ("Products", "Companies") — the
   * same checklist, so a second picker primitive is not needed.
   */
  title?: string;
  /** The singular noun in the disabled-reason sentences. Defaults to `column`. */
  itemNoun?: string;
  /**
   * `false` drops the grip and the move buttons — for a list whose order is
   * not the reader's to choose (a matrix's years run in date order). Default `true`.
   */
  reorderable?: boolean;
  /** `false` drops the freeze controls on every row. Default `true`. */
  freezable?: boolean;
  /** Extra controls rendered under the heading, above the list (e.g. a switch). */
  headerSlot?: React.ReactNode;
}

const VIEWPORT_MARGIN = 8;
const MIN_WIDTH = 288;

export function ColumnManager({
  layout,
  labelOf,
  lockedKeys = [],
  lockReasonOf,
  pinnedKeys = [],
  className,
  title = 'Columns',
  itemNoun = 'column',
  reorderable = true,
  freezable = true,
  headerSlot,
}: ColumnManagerProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragKey = useRef<string | null>(null);
  const titleId = useId();

  const locked = new Set(lockedKeys);
  const pinned = new Set(pinnedKeys);
  const visibleCount = layout.orderedKeys.filter((key) => layout.isVisible(key)).length;

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    // Measured, not assumed — see the header note.
    const measured = panel?.offsetWidth ?? MIN_WIDTH;
    const width = Math.min(Math.max(measured, MIN_WIDTH), window.innerWidth - VIEWPORT_MARGIN * 2);
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.left),
      window.innerWidth - width - VIEWPORT_MARGIN,
    );
    setPosition({ top: rect.bottom + 4, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      /*
       * 🔴 **BOUND IN THE CAPTURE PHASE, AND THAT IS THE WHOLE FIX.**
       *
       * ⚠️ *SUPERSEDED: "Stopped so a table inside a Modal does not also
       * close."* That comment described an intention this listener could not
       * carry out. **React delegates from the PORTAL CONTAINER, which is
       * `document.body` — one node BELOW `document`.** So on a bubbling
       * listener the order was: React's synthetic handler at `body` → `Modal`
       * closes → `Modal` calls `stopPropagation()`, which for a SyntheticEvent
       * also stops the NATIVE event → this handler never runs at all. Pressing
       * Escape over an open popover closed the entire dialog underneath it.
       *
       * Capture runs the other way — `document` before `body` — so this fires
       * first and its `stopPropagation()` keeps the key from reaching the
       * modal. The popover closes; the dialog does not.
       *
       * ⛔ **THE OBVIOUS FIX IS WORSE.** Silencing the modal from a React
       * ancestor instead means calling `SyntheticEvent.stopPropagation()`,
       * which stops the native event too — so Escape would then close NEITHER.
       *
       * ⚠️ The listener is only bound while the popover is open, so a closed
       * popover intercepts nothing and Escape still closes a dialog normally.
       *
       * Found by the page that mounts these tables inside `Modal`, which had to
       * work around it locally. Fixed here so no caller has to know.
       */
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  const rows = layout.orderedKeys;

  /*
   * ✅ **SHOW ALL — owner's instruction, 2026-09-13: every multi-tick control in
   * the app offers a select-all.** Modelled on `ColumnFilter`'s pair: a
   * `Button` rather than an option row, and **disabled rather than hidden** when
   * there is nothing for it to act on. A control that vanishes whenever it would
   * be a no-op reads as a bug; a disabled one reads as a state.
   *
   * ⛔ **WORDED AS CHROME, NOT AS A FILTER — and the wording is the decision
   * here, not the styling.** `ColumnFilter` says "Select all" because it is
   * choosing VALUES, and a tick there really does change which rows come back.
   * This bank changes which columns THIS READER sees and narrows nothing: same
   * records, same figures, same counts. Borrowing a filter's verb for a control
   * that touches no data is the one misreading this control can produce.
   * **"Show all" is also this component's OWN existing word** — the footer it
   * sits in already reads `All shown` / `N hidden` and the trigger badge already
   * reads `All`, so a second verb for one concept would be the drift rather than
   * the consistency.
   *
   * ⛔ **THERE IS DELIBERATELY NO "HIDE ALL", AND IT IS NOT AN OMISSION FOR A
   * LATER PASS TO FINISH.** `useColumnLayout.toggleVisible` REFUSES to hide the
   * last visible column — a blank table reads as broken — and it refuses a
   * LOCKED key outright. So a hide-everything loop would stop at whichever
   * column the iteration happened to reach last, plus every locked one: a button
   * labelled for one outcome producing another, picked by nobody. **The clear
   * direction cannot be worded honestly at this control, so it is not offered**
   * — and `Reset` beside it already covers the only wholesale undo that has a
   * truthful name.
   */

  /*
   * ⚠️ **A LOCKED COLUMN CAN BE HIDDEN; IT JUST CANNOT BE UNHIDDEN FROM HERE.**
   * `toggleVisible` returns early on a locked key, and the hidden set is stored
   * against the account — so a column somebody hid yesterday and the page locked
   * today arrives hidden and immovable. It still counts towards `hiddenCount`,
   * which is why the button's disabled test is this list and not that number:
   * enabling a control over work it cannot perform is the affordance-with-one-
   * outcome that the Xero `sync` button is this repo's standing example of.
   */
  const hiddenUnlocked = rows.filter((key) => !layout.isVisible(key) && !locked.has(key));
  const lockedHiddenOnly = hiddenUnlocked.length === 0 && layout.hiddenCount > 0;

  /*
   * ✅ **ONE STATE CHANGE, ONE REQUEST — `layout.showAll()`, shipped on the
   * controller.** *SUPERSEDED: "⚠️ **ONE CALL PER COLUMN, BECAUSE THE CONTROLLER
   * PUBLISHES NO BULK VERB** … 🔴 **THE COST IS ON THE WIRE** … each carries the
   * WHOLE `hidden` array, so they do not merge — the last one to ARRIVE wins …
   * ⛔ **THE FIX IS A BULK `showAll` ON THE CONTROLLER … which this pass does not
   * own.**"* That fix is the verb this now calls, and the race it described is
   * closed rather than narrowed.
   *
   * ⚠️ **`hiddenUnlocked` SURVIVES, AND IT IS NOT A SECOND IMPLEMENTATION.** It
   * no longer drives the write — it is the DISABLED test and the input to
   * `lockedHiddenOnly`, which is a question about this component's own
   * `lockedKeys` prop that the controller cannot answer for it. ⛔ **Do not
   * "finish" the change by deleting it in favour of `layout.hiddenCount`**: a
   * column somebody hid yesterday and the page locked today counts towards that
   * number and cannot be shown, so the button would be enabled over work it
   * cannot perform — the affordance-with-one-outcome this app forbids, and the
   * exact case the `Every hidden column is locked` sentence exists for.
   */

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="secondary"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        // Until the saved layout has been read the controller refuses changes,
        // so the trigger says so rather than opening a panel that ignores clicks.
        loading={layout.loading}
        onClick={() => setOpen((value) => !value)}
        className={cx('gap-1.5', className)}
      >
        <ColumnsIcon />
        {title}
        {layout.hiddenCount > 0 ? (
          <Badge tone="warning">{layout.hiddenCount} hidden</Badge>
        ) : (
          <Badge tone="neutral">All</Badge>
        )}
      </Button>

      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-labelledby={titleId}
              style={{ position: 'fixed', top: position.top, left: position.left }}
              className="z-[60] flex max-h-[min(24rem,calc(100vh-6rem))] w-72 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md"
            >
              <p id={titleId} className="px-3 py-2 text-xs font-semibold text-fg-muted">
                {title}
              </p>
              {headerSlot ? (
                <div className="border-b border-border px-3 pb-2">{headerSlot}</div>
              ) : null}
              <ul className="m-0 min-w-0 list-none overflow-y-auto p-0">
                {rows.map((key, index) => {
                  const isLocked = locked.has(key);
                  const visible = layout.isVisible(key);
                  const side = layout.frozenSideOf(key);
                  const lastVisible = visible && visibleCount === 1;
                  const hideReason = isLocked
                    ? (lockReasonOf?.(key) ?? `This ${itemNoun} cannot be hidden`)
                    : lastVisible
                      ? `A table must keep at least one ${itemNoun}`
                      : undefined;

                  return (
                    <li
                      key={key}
                      draggable={reorderable && !isLocked}
                      onDragStart={() => {
                        dragKey.current = key;
                      }}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        const from = dragKey.current;
                        dragKey.current = null;
                        if (from !== null && from !== key) layout.moveTo(from, index);
                      }}
                      className="flex min-w-0 items-center gap-1.5 px-2 py-1.5"
                    >
                      {reorderable ? (
                      <>
                      <span
                        aria-hidden
                        title="Drag to reorder"
                        className="cursor-grab text-fg-subtle pointer-coarse:hidden"
                      >
                        <GripIcon />
                      </span>
                      <span className="flex flex-col">
                        <IconButton
                          label={`Move ${labelOf(key)} up`}
                          disabled={isLocked || index === 0}
                          onClick={() => layout.move(key, -1)}
                        >
                          <ChevronIcon up />
                        </IconButton>
                        <IconButton
                          label={`Move ${labelOf(key)} down`}
                          disabled={isLocked || index === rows.length - 1}
                          onClick={() => layout.move(key, 1)}
                        >
                          <ChevronIcon />
                        </IconButton>
                      </span>
                      </>
                      ) : null}
                      <Checkbox
                        checked={visible}
                        disabled={isLocked || lastVisible}
                        {...(hideReason ? { title: hideReason } : {})}
                        onChange={() => layout.toggleVisible(key)}
                        label={labelOf(key)}
                        className="min-w-0 flex-1"
                      />
                      {/*
                        ⛔ **NO FREEZE CONTROLS ON A `pinned` COLUMN — AND NOTE
                        IT IS `pinned`, NOT `locked`.** A pinned cell is already
                        `sticky right-0`, so offering "freeze left" would put TWO
                        CONFLICTING POSITION RULES on one cell and the winner is
                        whichever the browser resolves last, not a decision
                        anybody made. Absent rather than disabled: that edge is
                        not the reader's to choose at all, so there is no state a
                        disabled control would explain.

                        🔴 **THIS READ `isLocked` FOR ONE REVISION AND THAT BROKE
                        THE FEATURE'S MAIN USE.** Pages lock their IDENTIFYING
                        column — Product, Company, Client — because hiding it
                        leaves figures describing a record nobody can name. That
                        column is also the one a reader most wants frozen to the
                        left, so keying this on `locked` made freeze-left
                        unreachable exactly where it matters most.
                      */}
                      {!freezable || pinned.has(key) ? null : (
                        <>
                          <IconButton
                            label={`Freeze ${labelOf(key)} to the left`}
                            pressed={side === 'left'}
                            onClick={() =>
                              layout.setFrozen(key, side === 'left' ? undefined : 'left')
                            }
                          >
                            <FreezeIcon side="left" />
                          </IconButton>
                          <IconButton
                            label={`Freeze ${labelOf(key)} to the right`}
                            pressed={side === 'right'}
                            onClick={() =>
                              layout.setFrozen(key, side === 'right' ? undefined : 'right')
                            }
                          >
                            <FreezeIcon side="right" />
                          </IconButton>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
                <span className="text-xs text-fg-muted">
                  {layout.hiddenCount > 0 ? `${layout.hiddenCount} hidden` : 'All shown'}
                </span>
                <span className="flex items-center gap-1">
                  {/*
                    ⚠️ **THE TITLE IS ON THE WRAPPER AND NOT ON THE BUTTON, AND
                    THAT IS NOT A STYLING CHOICE.** `Button` carries
                    `disabled:pointer-events-none`, so a `title` on a DISABLED
                    button never receives the hover that would show it — the
                    sentence would sit in the DOM, unreachable by the only reader
                    who needs it. The wrapper still takes pointer events, which
                    makes it the one place a why-this-is-disabled sentence can
                    actually render. Same channel `ColumnManager` already uses for
                    a locked column, different element for a different reason.

                    ⚠️ **AND IT IS THE ONLY DISABLED CASE THAT NEEDS ONE.** When
                    nothing is hidden at all the reason is already on screen, one
                    element to the left, reading `All shown` — restating it beside
                    the control would be helper text, which this file does not
                    carry.
                  */}
                  <span
                    className="inline-flex"
                    {...(lockedHiddenOnly ? { title: `Every hidden ${itemNoun} is locked` } : {})}
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={hiddenUnlocked.length === 0}
                      onClick={layout.showAll}
                    >
                      Show all
                    </Button>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={layout.isDefault}
                    onClick={layout.reset}
                  >
                    Reset
                  </Button>
                </span>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function IconButton({
  label,
  disabled,
  pressed,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  pressed?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip label={label}>
    <button
      type="button"
      aria-label={label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'grid h-5 w-5 place-items-center rounded text-fg-subtle transition-colors duration-150 ease-out motion-reduce:transition-none',
        'hover:bg-surface-hover hover:text-fg disabled:pointer-events-none disabled:opacity-40',
        // Neutral selected state; `aria-pressed` and the icon carry the meaning.
        pressed && 'bg-surface-active text-fg',
      )}
    >
      {children}
    </button>
    </Tooltip>
  );
}

function ColumnsIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <path d="M6.5 2.5v11M10 2.5v11" />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
      <circle cx="6" cy="4" r="1" />
      <circle cx="10" cy="4" r="1" />
      <circle cx="6" cy="8" r="1" />
      <circle cx="10" cy="8" r="1" />
      <circle cx="6" cy="12" r="1" />
      <circle cx="10" cy="12" r="1" />
    </svg>
  );
}

function ChevronIcon({ up }: { up?: boolean }) {
  return (
    <svg viewBox="0 0 16 16" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d={up ? 'M3 10l5-5 5 5' : 'M3 6l5 5 5-5'} />
    </svg>
  );
}

/** An arrow into a wall — the same shape the reference design uses. */
function FreezeIcon({ side }: { side: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      {side === 'left' ? (
        <>
          <path d="M2.5 3v10" />
          <path d="M13 8H5.5M8 4.5L5 8l3 3.5" />
        </>
      ) : (
        <>
          <path d="M13.5 3v10" />
          <path d="M3 8h7.5M8 4.5L11 8l-3 3.5" />
        </>
      )}
    </svg>
  );
}
